import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Meta from "../Meta/Meta";

export default function AboutPage() {
  const { t, i18n } = useTranslation(["ui"]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const paragraphs = t("about.paragraphs", { returnObjects: true });
  const benefits = t("about.memberBenefits", { returnObjects: true });
  const support = t("about.practicalSupport", { returnObjects: true });
  const team = t("about.teamPoints", { returnObjects: true });

  return (
    <>
      <Meta
        title="ALGermanist - About Us"
        description="Learn about ALGermanist's mission to help learners master German with expert teachers and personalized support."
        keywords="about ALGermanist, language school, German courses"
      />
      <Box sx={{ mt: 10, mb: 8 }}>
        {/* Hero Section */}
        <Box
          sx={{
            textAlign: "center",
            mb: 6,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: { xs: 260, md: 520 },
              bgcolor: "#f5f5f5",
              backgroundImage:
                "linear-gradient(180deg, rgba(0,0,0,.35), rgba(0,0,0,.35)), url('/home-heidelberg-bg.png')",
              backgroundSize: "cover",
              backgroundPosition: {
                xs: "38% 9%",
                sm: "42% 9%",
                md: "center 9%",
              },
              mb: 4,
              mt: -10,
            }}
          />
          <Box
            component={"img"}
            src="/al-germanist-logo.png"
              sx={{
                width: 104,
                height: 104,
                objectFit: "contain",
                mb: 4,
              }}
          />
          <Typography
            variant={isMobile ? "h6" : "h4"}
            fontWeight="bold"
            sx={{ mb: 2, textAlign: "center", mx: { xs: 2, md: 0 } }}
          >
            {t("about.title")}
          </Typography>
          <Typography
            variant={isMobile ? "body2" : "body1"}
            color="text.secondary"
            sx={{
              maxWidth: 900,
              mx: { xs: 2, md: "auto" },
              lineHeight: 2,
              textAlign: { xs: "start", sm: "center" },
              fontSize: "1.1rem",
              px: 2,
              mb: { xs: 10, md: 8 },
              whiteSpace: "pre-line",
            }}
          >
            {Array.isArray(paragraphs) && paragraphs.map((paragraph) => (
              <Box component="span" display="block" mb={2} key={paragraph}>{paragraph}</Box>
            ))}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 3, md: 4 }, mb: 7 }}>
          {[
            ["about.memberBenefitsTitle", benefits],
            ["about.practicalSupportTitle", support],
            ["about.teamTitle", team],
          ].map(([titleKey, items]) => (
            <Box key={titleKey} sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>{t(titleKey)}</Typography>
              <Box component="ul" sx={{ m: 0, ps: 3, lineHeight: 2 }}>
                {Array.isArray(items) && items.map((item) => <li key={item}>{item}</li>)}
              </Box>
            </Box>
          ))}
          <Typography sx={{ lineHeight: 2, mb: 2 }}>{t("about.future")}</Typography>
          <Typography sx={{ lineHeight: 2, fontWeight: 700 }}>{t("about.goal")}</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Button
            component={Link}
            to="/#services"
            variant="contained"
            endIcon={
              <ArrowForwardRoundedIcon
                sx={{
                  transform: i18n.dir() === "rtl" ? "rotate(180deg)" : "none",
                }}
              />
            }
            sx={{
              px: { xs: 3.5, sm: 5 },
              py: { xs: 1.6, sm: 1.8 },
              borderRadius: "999px",
              fontSize: { xs: "1rem", sm: "1.1rem" },
              fontWeight: 800,
              textTransform: "none",
              color: "#111",
              background: "linear-gradient(135deg, #FFC107 0%, #FFD54F 100%)",
              boxShadow: "0 12px 30px rgba(255, 193, 7, 0.32)",
              transition: "transform 180ms ease, box-shadow 180ms ease",
              "&:hover": {
                color: "#111",
                background: "linear-gradient(135deg, #F5B900 0%, #FFC928 100%)",
                boxShadow: "0 16px 36px rgba(255, 193, 7, 0.42)",
                transform: "translateY(-3px)",
              },
            }}
          >
            {t("about.servicesButton")}
          </Button>
        </Box>
      </Box>
    </>
  );
}
